import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Chip,
  Avatar,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  LinearProgress,
  Tooltip
} from '@mui/material';
import {
  Task as TaskIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayArrowIcon,
  Warning as WarningIcon,
  Visibility as VisibilityIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Comment as CommentIcon,
  AttachFile as AttachFileIcon,
  Clear as ClearIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[8],
  },
}));

const GradientHeader = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(3),
}));

const PriorityChip = styled(Chip)(({ priority }) => ({
  fontWeight: 'bold',
  ...(priority === 'উচ্চ' && {
    backgroundColor: '#ffebee',
    color: '#c62828',
    border: '1px solid #ef5350'
  }),
  ...(priority === 'মাঝারি' && {
    backgroundColor: '#fff3e0',
    color: '#ef6c00',
    border: '1px solid #ff9800'
  }),
  ...(priority === 'নিম্ন' && {
    backgroundColor: '#e8f5e8',
    color: '#2e7d32',
    border: '1px solid #4caf50'
  })
}));

const EmployeeTasks = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('সব');
  const [filterPriority, setFilterPriority] = useState('সব');
  const [sortBy, setSortBy] = useState('dueDate');
  const [sortOrder, setSortOrder] = useState('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  // Mock task data
  const [tasks, setTasks] = useState([
    {
      id: 1,
      title: 'গ্রাহক অর্ডার যাচাই করুন',
      description: 'নতুন গ্রাহকদের অর্ডারগুলি যাচাই করে নিশ্চিত করুন যে সব তথ্য সঠিক আছে।',
      category: 'অর্ডার ম্যানেজমেন্ট',
      priority: 'উচ্চ',
      status: 'চলমান',
      assignedBy: 'ম্যানেজার সাহেব',
      dueDate: '২০২৪-০১-২৫',
      createdDate: '২০২৪-০১-২০',
      estimatedTime: '২ ঘণ্টা',
      actualTime: '১.৫ ঘণ্টা',
      progress: 75,
      tags: ['জরুরি', 'গ্রাহক সেবা'],
      comments: 3,
      attachments: 1,
      isStarred: true
    },
    {
      id: 2,
      title: 'নতুন পণ্য ক্যাটালগ আপডেট',
      description: 'শীতকালীন পণ্যের তালিকা আপডেট করুন এবং দাম নির্ধারণ করুন।',
      category: 'ইনভেন্টরি',
      priority: 'মাঝারি',
      status: 'নতুন',
      assignedBy: 'টিম লিড',
      dueDate: '২০২৪-০১-২৮',
      createdDate: '২০২৪-০১-২২',
      estimatedTime: '৪ ঘণ্টা',
      actualTime: '০ ঘণ্টা',
      progress: 0,
      tags: ['পণ্য', 'আপডেট'],
      comments: 1,
      attachments: 2,
      isStarred: false
    },
    {
      id: 3,
      title: 'গ্রাহক সহায়তা কল সম্পন্ন',
      description: 'রহিমা বেগমের সাথে ফোনে কথা বলে তার সমস্যার সমাধান করুন।',
      category: 'কাস্টমার সাপোর্ট',
      priority: 'উচ্চ',
      status: 'সম্পন্ন',
      assignedBy: 'সাপোর্ট ম্যানেজার',
      dueDate: '২০২৪-০১-২৩',
      createdDate: '২০২৪-০১-২৩',
      estimatedTime: '৩০ মিনিট',
      actualTime: '৪৫ মিনিট',
      progress: 100,
      tags: ['ফোন কল', 'সমস্যা সমাধান'],
      comments: 5,
      attachments: 0,
      isStarred: false
    },
    {
      id: 4,
      title: 'সাপ্তাহিক রিপোর্ট প্রস্তুতি',
      description: 'এই সপ্তাহের কার্যকলাপের উপর বিস্তারিত রিপোর্ট তৈরি করুন।',
      category: 'রিপোর্টিং',
      priority: 'নিম্ন',
      status: 'অপেক্ষমান',
      assignedBy: 'হেড অফিস',
      dueDate: '২০২৪-০১-২৬',
      createdDate: '২০২৪-০১-২১',
      estimatedTime: '৩ ঘণ্টা',
      actualTime: '১ ঘণ্টা',
      progress: 25,
      tags: ['রিপোর্ট', 'সাপ্তাহিক'],
      comments: 0,
      attachments: 3,
      isStarred: true
    },
    {
      id: 5,
      title: 'নতুন কর্মচারী প্রশিক্ষণ',
      description: 'নতুন জয়নাল নামক কর্মচারীকে সিস্টেম ব্যবহারের প্রশিক্ষণ দিন।',
      category: 'প্রশিক্ষণ',
      priority: 'মাঝারি',
      status: 'বাতিল',
      assignedBy: 'এইচআর ম্যানেজার',
      dueDate: '২০২৪-০১-২৪',
      createdDate: '২০২৪-০১-১৯',
      estimatedTime: '২ ঘণ্টা',
      actualTime: '০ ঘণ্টা',
      progress: 0,
      tags: ['প্রশিক্ষণ', 'নতুন কর্মী'],
      comments: 2,
      attachments: 1,
      isStarred: false
    }
  ]);

  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    category: '',
    priority: 'মাঝারি',
    dueDate: '',
    estimatedTime: '',
    tags: []
  });

  // Filter and sort tasks
  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         task.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'সব' || task.status === filterStatus;
    const matchesPriority = filterPriority === 'সব' || task.priority === filterPriority;
    
    // Tab-based filtering
    if (currentTab === 1) { // Today's Tasks
      const today = new Date().toISOString().split('T')[0];
      const taskDate = task.dueDate;
      return matchesSearch && matchesStatus && matchesPriority && taskDate === today;
    } else if (currentTab === 2) { // Completed Tasks
      return matchesSearch && matchesPriority && task.status === 'সম্পন্ন';
    } else if (currentTab === 3) { // Overdue Tasks
      const today = new Date();
      const taskDueDate = new Date(task.dueDate);
      return matchesSearch && matchesStatus && matchesPriority && 
             taskDueDate < today && task.status !== 'সম্পন্ন';
    }
    
    return matchesSearch && matchesStatus && matchesPriority; // All Tasks
  });

  const sortedTasks = [...filteredTasks].sort((a, b) => {
    let aValue = a[sortBy];
    let bValue = b[sortBy];
    
    if (sortBy === 'dueDate') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }
    
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const paginatedTasks = sortedTasks.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreateTask = () => {
    const task = {
      id: Date.now(),
      ...newTask,
      status: 'নতুন',
      assignedBy: 'আমি',
      createdDate: new Date().toISOString().split('T')[0],
      progress: 0,
      comments: 0,
      attachments: 0,
      isStarred: false,
      actualTime: '০ ঘণ্টা'
    };
    
    setTasks([...tasks, task]);
    setNewTask({
      title: '',
      description: '',
      category: '',
      priority: 'মাঝারি',
      dueDate: '',
      estimatedTime: '',
      tags: []
    });
    setShowCreateDialog(false);
  };

  const handleToggleStar = (taskId) => {
    setTasks(tasks.map(task => 
      task.id === taskId ? { ...task, isStarred: !task.isStarred } : task
    ));
  };

  const handleStatusChange = (taskId, newStatus) => {
    setTasks(tasks.map(task => {
      if (task.id === taskId) {
        return {
          ...task,
          status: newStatus,
          progress: newStatus === 'সম্পন্ন' ? 100 : 
                   newStatus === 'চলমান' ? Math.max(task.progress, 25) : 
                   task.progress
        };
      }
      return task;
    }));
  };

  const getTaskStats = () => {
    return {
      total: tasks.length,
      completed: tasks.filter(t => t.status === 'সম্পন্ন').length,
      inProgress: tasks.filter(t => t.status === 'চলমান').length,
      pending: tasks.filter(t => t.status === 'অপেক্ষমান').length,
      overdue: tasks.filter(t => {
        const today = new Date();
        const taskDueDate = new Date(t.dueDate);
        return taskDueDate < today && t.status !== 'সম্পন্ন';
      }).length
    };
  };

  const stats = getTaskStats();

  const tabData = [
    { label: 'সব কাজ', count: stats.total },
    { label: 'আজকের কাজ', count: tasks.filter(t => t.dueDate === new Date().toISOString().split('T')[0]).length },
    { label: 'সম্পন্ন কাজ', count: stats.completed },
    { label: 'বিলম্বিত কাজ', count: stats.overdue }
  ];

  return (
    <Box>
      <GradientHeader>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
              আমার কাজসমূহ
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              আপনার সব কাজ এক জায়গায় পরিচালনা করুন
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowCreateDialog(true)}
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.3)'
              }
            }}
          >
            নতুন কাজ তৈরি
          </Button>
        </Box>
      </GradientHeader>

      {/* Task Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
              <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 48, height: 48 }}>
                <TaskIcon />
              </Avatar>
              <Box>
                <Typography color="text.secondary" variant="body2" gutterBottom>
                  মোট কাজ
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {stats.total}
                </Typography>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
              <Avatar sx={{ bgcolor: 'success.main', mr: 2, width: 48, height: 48 }}>
                <CheckCircleIcon />
              </Avatar>
              <Box>
                <Typography color="text.secondary" variant="body2" gutterBottom>
                  সম্পন্ন
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {stats.completed}
                </Typography>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
              <Avatar sx={{ bgcolor: 'info.main', mr: 2, width: 48, height: 48 }}>
                <PlayArrowIcon />
              </Avatar>
              <Box>
                <Typography color="text.secondary" variant="body2" gutterBottom>
                  চলমান
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {stats.inProgress}
                </Typography>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
              <Avatar sx={{ bgcolor: 'error.main', mr: 2, width: 48, height: 48 }}>
                <WarningIcon />
              </Avatar>
              <Box>
                <Typography color="text.secondary" variant="body2" gutterBottom>
                  বিলম্বিত
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {stats.overdue}
                </Typography>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      {/* Task Management Section */}
      <Paper sx={{ width: '100%' }}>
        {/* Task Tabs */}
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minWidth: 120,
              fontWeight: 500
            }
          }}
        >
          {tabData.map((tab, index) => (
            <Tab 
              key={index}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {tab.label}
                  <Badge badgeContent={tab.count} color="primary" max={99} />
                </Box>
              }
            />
          ))}
        </Tabs>

        {/* Search and Filter Bar */}
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="কাজ খুঁজুন..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>অবস্থা</InputLabel>
                <Select
                  value={filterStatus}
                  label="অবস্থা"
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <MenuItem value="সব">সব</MenuItem>
                  <MenuItem value="নতুন">নতুন</MenuItem>
                  <MenuItem value="চলমান">চলমান</MenuItem>
                  <MenuItem value="সম্পন্ন">সম্পন্ন</MenuItem>
                  <MenuItem value="অপেক্ষমান">অপেক্ষমান</MenuItem>
                  <MenuItem value="বাতিল">বাতিল</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>অগ্রাধিকার</InputLabel>
                <Select
                  value={filterPriority}
                  label="অগ্রাধিকার"
                  onChange={(e) => setFilterPriority(e.target.value)}
                >
                  <MenuItem value="সব">সব</MenuItem>
                  <MenuItem value="উচ্চ">উচ্চ</MenuItem>
                  <MenuItem value="মাঝারি">মাঝারি</MenuItem>
                  <MenuItem value="নিম্ন">নিম্ন</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>সাজান</InputLabel>
                <Select
                  value={sortBy}
                  label="সাজান"
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="dueDate">সময়সীমা</MenuItem>
                  <MenuItem value="priority">অগ্রাধিকার</MenuItem>
                  <MenuItem value="status">অবস্থা</MenuItem>
                  <MenuItem value="title">শিরোনাম</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  color="primary"
                >
                  {sortOrder === 'asc' ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </IconButton>
                <IconButton
                  onClick={() => {
                    setSearchTerm('');
                    setFilterStatus('সব');
                    setFilterPriority('সব');
                    setSortBy('dueDate');
                    setSortOrder('asc');
                  }}
                  color="secondary"
                >
                  <ClearIcon />
                </IconButton>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Task Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                <TableCell>কাজের নাম</TableCell>
                <TableCell>ক্যাটাগরি</TableCell>
                <TableCell>অগ্রাধিকার</TableCell>
                <TableCell>অবস্থা</TableCell>
                <TableCell>অগ্রগতি</TableCell>
                <TableCell>সময়সীমা</TableCell>
                <TableCell>নিয়োগকারী</TableCell>
                <TableCell>কাজ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedTasks.map((task) => (
                <TableRow key={task.id} hover>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleToggleStar(task.id)}
                      color={task.isStarred ? 'warning' : 'default'}
                    >
                      {task.isStarred ? <StarIcon /> : <StarBorderIcon />}
                    </IconButton>
                  </TableCell>
                  
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {task.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {task.description.length > 50 
                          ? `${task.description.substring(0, 50)}...` 
                          : task.description}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                        {task.tags.map((tag, index) => (
                          <Chip
                            key={index}
                            label={tag}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: '20px' }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    <Chip 
                      label={task.category} 
                      variant="outlined" 
                      size="small" 
                    />
                  </TableCell>
                  
                  <TableCell>
                    <PriorityChip 
                      label={task.priority} 
                      priority={task.priority}
                      size="small"
                    />
                  </TableCell>
                  
                  <TableCell>
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                      <Select
                        value={task.status}
                        onChange={(e) => handleStatusChange(task.id, e.target.value)}
                        variant="outlined"
                      >
                        <MenuItem value="নতুন">নতুন</MenuItem>
                        <MenuItem value="চলমান">চলমান</MenuItem>
                        <MenuItem value="সম্পন্ন">সম্পন্ন</MenuItem>
                        <MenuItem value="অপেক্ষমান">অপেক্ষমান</MenuItem>
                        <MenuItem value="বাতিল">বাতিল</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={task.progress} 
                        sx={{ width: 60 }}
                      />
                      <Typography variant="caption">{task.progress}%</Typography>
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2">
                      {task.dueDate}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {task.estimatedTime}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2">
                      {task.assignedBy}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="দেখুন">
                        <IconButton 
                          size="small" 
                          onClick={() => {
                            setSelectedTask(task);
                            setShowDetailsDialog(true);
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="সম্পাদনা">
                        <IconButton size="small" color="primary">
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      
                      {task.comments > 0 && (
                        <Tooltip title={`${task.comments} মন্তব্য`}>
                          <IconButton size="small">
                            <Badge badgeContent={task.comments} color="primary" max={9}>
                              <CommentIcon />
                            </Badge>
                          </IconButton>
                        </Tooltip>
                      )}
                      
                      {task.attachments > 0 && (
                        <Tooltip title={`${task.attachments} সংযুক্তি`}>
                          <IconButton size="small">
                            <Badge badgeContent={task.attachments} color="secondary" max={9}>
                              <AttachFileIcon />
                            </Badge>
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredTasks.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="প্রতি পাতায় সারি:"
        />
      </Paper>

      {/* Create Task Dialog */}
      <Dialog open={showCreateDialog} onClose={() => setShowCreateDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>নতুন কাজ তৈরি</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="কাজের শিরোনাম"
                  value={newTask.title}
                  onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                  variant="outlined"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="কাজের বিবরণ"
                  value={newTask.description}
                  onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                  variant="outlined"
                  multiline
                  rows={3}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>ক্যাটাগরি</InputLabel>
                  <Select
                    value={newTask.category}
                    label="ক্যাটাগরি"
                    onChange={(e) => setNewTask({...newTask, category: e.target.value})}
                  >
                    <MenuItem value="অর্ডার ম্যানেজমেন্ট">অর্ডার ম্যানেজমেন্ট</MenuItem>
                    <MenuItem value="ইনভেন্টরি">ইনভেন্টরি</MenuItem>
                    <MenuItem value="কাস্টমার সাপোর্ট">কাস্টমার সাপোর্ট</MenuItem>
                    <MenuItem value="রিপোর্টিং">রিপোর্টিং</MenuItem>
                    <MenuItem value="প্রশিক্ষণ">প্রশিক্ষণ</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>অগ্রাধিকার</InputLabel>
                  <Select
                    value={newTask.priority}
                    label="অগ্রাধিকার"
                    onChange={(e) => setNewTask({...newTask, priority: e.target.value})}
                  >
                    <MenuItem value="উচ্চ">উচ্চ</MenuItem>
                    <MenuItem value="মাঝারি">মাঝারি</MenuItem>
                    <MenuItem value="নিম্ন">নিম্ন</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="সময়সীমা"
                  type="date"
                  value={newTask.dueDate}
                  onChange={(e) => setNewTask({...newTask, dueDate: e.target.value})}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="আনুমানিক সময়"
                  value={newTask.estimatedTime}
                  onChange={(e) => setNewTask({...newTask, estimatedTime: e.target.value})}
                  variant="outlined"
                  placeholder="যেমন: ২ ঘণ্টা"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateDialog(false)}>বাতিল</Button>
          <Button 
            variant="contained" 
            onClick={handleCreateTask}
            disabled={!newTask.title || !newTask.category}
          >
            সংরক্ষণ
          </Button>
        </DialogActions>
      </Dialog>

      {/* Task Details Dialog */}
      <Dialog open={showDetailsDialog} onClose={() => setShowDetailsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>কাজের বিস্তারিত</DialogTitle>
        <DialogContent>
          {selectedTask && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    {selectedTask.title}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {selectedTask.description}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    ক্যাটাগরি
                  </Typography>
                  <Typography variant="body1">{selectedTask.category}</Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    অগ্রাধিকার
                  </Typography>
                  <PriorityChip 
                    label={selectedTask.priority} 
                    priority={selectedTask.priority}
                    size="small"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    অবস্থা
                  </Typography>
                  <Chip 
                    label={selectedTask.status} 
                    size="small"
                    color="primary"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    অগ্রগতি
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={selectedTask.progress} 
                      sx={{ width: 100 }}
                    />
                    <Typography variant="body2">{selectedTask.progress}%</Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    সময়সীমা
                  </Typography>
                  <Typography variant="body1">{selectedTask.dueDate}</Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    নিয়োগকারী
                  </Typography>
                  <Typography variant="body1">{selectedTask.assignedBy}</Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    আনুমানিক সময়
                  </Typography>
                  <Typography variant="body1">{selectedTask.estimatedTime}</Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    প্রকৃত সময়
                  </Typography>
                  <Typography variant="body1">{selectedTask.actualTime}</Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    ট্যাগসমূহ
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {selectedTask.tags.map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDetailsDialog(false)}>বন্ধ করুন</Button>
          <Button variant="contained" color="primary">
            সম্পাদনা
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeeTasks; 